import { forwardRef, useMemo } from 'react';
import {
  default as BaseCheckbox,
  type CheckboxProps as BaseCheckboxProps,
} from '@mui/joy/Checkbox';
import Check02 from '@unifyapps/icons/outline/Check02';
import Minus from '@unifyapps/icons/outline/Minus';
import noop from 'lodash/noop';
import { clsx } from 'clsx';
import type { CheckboxProps } from './types';
import {
  checkboxRootVariants,
  checkboxCheckboxVariants,
  checkboxActionVariants,
  checkboxLabelVariants,
} from './styles';

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  const { className, size = 'sm', onChange, slotProps, ...restProps } = props;

  const iconSize = size === 'sm' ? 12 : 14;

  const checkboxSlotProps = useMemo<BaseCheckboxProps['slotProps']>(
    () => ({
      root: ({ checked, indeterminate, disabled, focusVisible }) => ({
        className: checkboxRootVariants({
          className: clsx(
            className,
            slotProps?.root && 'className' in slotProps.root ? slotProps.root.className : undefined,
          ),
          checked,
          indeterminate,
          disabled,
          focusVisible,
          size,
        }),
      }),
      checkbox: ({ checked, indeterminate, disabled, focusVisible }) => ({
        className: checkboxCheckboxVariants({
          checked,
          indeterminate,
          disabled,
          focusVisible,
          size,
          className:
            slotProps?.checkbox && 'className' in slotProps.checkbox
              ? slotProps.checkbox.className
              : undefined,
        }),
        'data-size': size,
      }),
      action: ({ checked, indeterminate, disabled, focusVisible }) => ({
        className: checkboxActionVariants({
          checked,
          indeterminate,
          disabled,
          focusVisible,
          size,
        }),
      }),
      label: ({ checked, indeterminate, disabled, focusVisible }) => ({
        className: checkboxLabelVariants({
          checked,
          indeterminate,
          disabled,
          focusVisible,
          size,
          className:
            slotProps?.label && 'className' in slotProps.label
              ? slotProps.label.className
              : undefined,
        }),
      }),
      input: {
        ref,
      },
    }),
    [ref, className, slotProps?.root, slotProps?.checkbox, slotProps?.label, size],
  );

  return (
    <BaseCheckbox
      checkedIcon={<Check02 height={iconSize} strokeWidth={2} width={iconSize} />}
      data-size={size}
      disabled={props.disabled || props.readOnly}
      indeterminateIcon={<Minus height={iconSize} width={iconSize} />}
      onChange={props.readOnly ? noop : onChange}
      ref={ref}
      size={size}
      slotProps={checkboxSlotProps}
      {...restProps}
    />
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
